import { UUID } from "angular2-uuid";
import { StoredCachedService } from "../models/stored-cached-service";
import { IStoredMapper } from "src/app/storage/mappers/stored-mapper";
import { DispatchedActionType } from "../../models/dispatched-action-type.enum";
import { ReferenceNumberMapper } from "src/app/storage/mappers/reference-number-mapper";
import { ServiceNoteMapper } from "src/app/storage/mappers/service-note-mapper";
import { ServiceNoteViewModel } from "src/app/view-models/orders/service-note.view-model";
import { AccessoryServiceMapper } from "src/app/storage/mappers/accessory-service-mapper";
import { AccessoryServiceViewModel } from "src/app/view-models/orders/accessory-service.view-model";
import { PieceMapper } from "src/app/storage/mappers/piece-mapper";
import { PieceViewModel } from "src/app/view-models/orders/piece.view-model";
import { LocationMapper } from "src/app/storage/mappers/location-mapper";
import { DatePartMapper } from "src/app/storage/mappers/date-part-mapper";
import { TimePartMapper } from "src/app/storage/mappers/time-part-mapper";
import { DocumentInformationMapper } from "src/app/storage/mappers/document-information-mapper";
import { CustomerMapper } from "src/app/storage/mappers/customer-mapper";
import { ServiceActionMapper } from "src/app/driver/storage/mappers/service-action-mapper";
import { StoredServiceAction } from "src/app/driver/storage/models/stored-service-action";
import { StoredDocumentInformation } from "src/app/storage/models/stored-document-information";
import { StoredReferenceNumber } from "src/app/storage/models/stored-orders/stored-reference-number";
import { StoredAccessoryService } from "src/app/storage/models/stored-orders/stored-accessory-service";
import { StoredPiece } from "src/app/storage/models/stored-orders/stored-piece";
import { DispatchedServiceViewModel } from "../../models/view-models/dispatched-service.view-model";
import { ServiceActionDto } from "../../models/dtos/service-action.dto";
import { DocumentInformationViewModel } from "src/app/view-models/document-information.view-model";
import { ReferenceNumberViewModel } from "src/app/view-models/reference-number.view-model";

/**
 * Class for mapping a DispatchedServiceViewModel to a StoredCachedService.
 */
export class CachedServiceMapper implements IStoredMapper<DispatchedServiceViewModel, StoredCachedService> {
	/** The mapper class for reference number data. */
	private readonly referenceNumberMapper: ReferenceNumberMapper = new ReferenceNumberMapper();

	/** The mapper class for service note data. */
	private readonly serviceNoteMapper: ServiceNoteMapper = new ServiceNoteMapper();

	/** The mapper class for accessory service data. */
	private readonly accessoryServiceMapper: AccessoryServiceMapper = new AccessoryServiceMapper();

	/** The mapper class for piece data. */
	private readonly pieceMapper: PieceMapper = new PieceMapper();

	/** The mapper class for location data. */
	private readonly locationMapper: LocationMapper = new LocationMapper();

	/** The mapper class for date part data. */
	private readonly datePartMapper: DatePartMapper = new DatePartMapper();

	/** The mapper class for time part data. */
	private readonly timePartMapper: TimePartMapper = new TimePartMapper();

	/** The mapper class for document information data. */
	private readonly documentInformationMapper: DocumentInformationMapper = new DocumentInformationMapper();

	/** The mapper class for customer data. */
	private readonly customerMapper: CustomerMapper = new CustomerMapper();

	/** The mapper class for service action data. */
	private readonly serviceActionMapper: ServiceActionMapper = new ServiceActionMapper();

	/**
	 * Maps a DispatchedServiceViewModel to a StoredCachedService.
	 * @param service The dispatched service data to convert.
	 * @param id The unique Id for the service.
	 * @param userName The username for the output StoredCachedService.
	 * @param crownId The crown Id for the output StoredCachedService.
	 * @param createdDate The date the storage model was created.
	 * @returns A StoredCachedService object.
	 */
	public ToStored(service: DispatchedServiceViewModel, id: UUID, userName: string, crownId: string, createdDate: Date | null): StoredCachedService {
		return {
			Id: id,
			UserName: userName,
			CrownId: crownId,
			CreatedDate: createdDate ?? new Date(),
			LegacyId: service.LegacyId,
			LegacyOrderId: service.LegacyOrderId,
			ServiceType: service.ServiceType,
			FlatPieces: service.FlatPieces,
			FlatWeight: service.FlatWeight,
			FlatSkids: service.FlatSkids,
			WeightUnitOfMeasure: service.WeightUnitOfMeasure,
			DimensionsUnitOfMeasure: service.DimensionsUnitOfMeasure,
			IsSpecial: service.IsSpecial,
			TimeZone: service.TimeZone,
			ServiceActions: service.ServiceActions.map((serviceAction: ServiceActionDto) => this.serviceActionMapper.ToStored(serviceAction)),
			DocumentsInformation: service.DocumentsInformation.map((documentInformation: DocumentInformationViewModel) => this.documentInformationMapper.ToStored(documentInformation)),
			ReferenceNumbers: service.ReferenceNumbers.map((referenceNumber: ReferenceNumberViewModel) => this.referenceNumberMapper.ToStored(referenceNumber)),
			AccessoryServices: service.AccessoryServices.map((accessoryServices: AccessoryServiceViewModel) => this.accessoryServiceMapper.ToStored(accessoryServices)),
			Pieces: service.Pieces.map((piece: PieceViewModel) => this.pieceMapper.ToStored(piece)),
			ServiceAddress: this.locationMapper.ToStored(service.ServiceAddress),
			SecondaryAddress: service.SecondaryAddress ? this.locationMapper.ToStored(service.SecondaryAddress) : null,
			ServiceDate: this.datePartMapper.ToStored(service.ServiceDate),
			ServiceWindowStartTime: service.ServiceWindowStartTime ? this.timePartMapper.ToStored(service.ServiceWindowStartTime) : null,
			ServiceWindowEndTime: service.ServiceWindowEndTime ? this.timePartMapper.ToStored(service.ServiceWindowEndTime) : null,
			ServiceDeadlineDate: service.ServiceDeadlineDate ? this.datePartMapper.ToStored(service.ServiceDeadlineDate) : null,
			ServiceNotes: service.ServiceNotes.map((serviceNote: ServiceNoteViewModel) => this.serviceNoteMapper.ToStored(serviceNote)),
			Customer: this.customerMapper.ToStored(service.Customer)
		};
	}

	/**
	 * Maps a StoredCachedService to a DispatchedServiceViewModel.
	 * @param service The stored cached service to convert.
	 * @returns A DispatchedServiceViewModel object.
	 */
	public FromStored(service: StoredCachedService): DispatchedServiceViewModel {
		return new DispatchedServiceViewModel({
			ServiceId: service.Id,
			LegacyId: service.LegacyId,
			LegacyOrderId: service.LegacyOrderId,
			ServiceType: service.ServiceType,
			FlatPieces: service.FlatPieces,
			FlatWeight: service.FlatWeight,
			FlatSkids: service.FlatSkids,
			WeightUnitOfMeasure: service.WeightUnitOfMeasure,
			DimensionsUnitOfMeasure: service.DimensionsUnitOfMeasure,
			IsSpecial: service.IsSpecial,
			TimeZone: service.TimeZone,
			Type: DispatchedActionType.Service,
			ServiceActions: service.ServiceActions.map((serviceAction: StoredServiceAction) => this.serviceActionMapper.FromStored(serviceAction)),
			DocumentsInformation: service.DocumentsInformation.map((documentInformation: StoredDocumentInformation) => this.documentInformationMapper.FromStored(documentInformation)),
			ReferenceNumbers: service.ReferenceNumbers.map((referenceNumber: StoredReferenceNumber) => this.referenceNumberMapper.FromStored(referenceNumber)),
			AccessoryServices: service.AccessoryServices.map((accessoryServices: StoredAccessoryService) => this.accessoryServiceMapper.FromStored(accessoryServices)),
			Pieces: service.Pieces.map((piece: StoredPiece) => this.pieceMapper.FromStored(piece)),
			ServiceAddress: this.locationMapper.FromStored(service.ServiceAddress),
			SecondaryAddress: service.SecondaryAddress ? this.locationMapper.FromStored(service.SecondaryAddress) : null,
			ServiceDate: this.datePartMapper.FromStored(service.ServiceDate).toString(),
			ServiceWindowStartTime: service.ServiceWindowStartTime ? this.timePartMapper.FromStored(service.ServiceWindowStartTime).toString() : null,
			ServiceWindowEndTime: service.ServiceWindowEndTime ? this.timePartMapper.FromStored(service.ServiceWindowEndTime).toString() : null,
			ServiceDeadlineDate: service.ServiceDeadlineDate ? this.datePartMapper.FromStored(service.ServiceDeadlineDate).toString() : null,
			ServiceNotes: service.ServiceNotes.map((serviceNote: ServiceNoteViewModel) => this.serviceNoteMapper.FromStored(serviceNote)),
			Customer: this.customerMapper.FromStored(service.Customer)
		});
	}
}